import _ from 'lodash';
import React, { Component, PropTypes } from 'react';
import classNames from 'classnames';
import ConfigGrid from './ConfigGrid';
import Header from './Header';
import Footer from './Footer';
import { unescapeScript } from 'utils/StringUtils';
import { isPositionStickySupported } from 'utils/DOMUtils';
import { buildMetaTags } from 'utils/SeoUtils';
import { AllHtmlEntities } from 'html-entities';
const entities = new AllHtmlEntities();

if (process.env.BROWSER) {
  require('styles/app.css');
}

// WEB-4964: decode text for menu
const decodeHTMLEntities = (text) => {
  return entities.decode(text);
};

const traverseMenu = (menu) => {
  if (_.isEmpty(menu)) {
    return;
  }
  for (let i = 0; i < menu.length; i++) {
    let menuI = menu[i];
    menuI.text = decodeHTMLEntities(menuI.text);

    if (!_.isEmpty(menuI.menu)) {
      traverseMenu(menuI.menu);
    }
  }
};


const decodeTextOfMenuItem = (data, path) => {
  const asJson = data || [];
  const components = _.get(asJson, path);
  const compIndex = _.findIndex(components, (comp) => {
    const { props: { menu } = {} } = comp;
    return !_.isEmpty(menu);
  });
  if (compIndex === -1) {
    return asJson;
  }

  const dataBypath = _.get(asJson, `${path}[${compIndex}].props.menu`);
  traverseMenu(dataBypath);
  return asJson;
};

class Page extends Component {
  static propTypes = {
    params: PropTypes.object,
    configPath: PropTypes.string,
    body: PropTypes.object
  }

  static contextTypes = {
    flux: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  componentWillMount() {
    const { configPath } = this.props;
    const { flux, config } = this.context;

    const pageContentClassification = _.get(config, `${configPath}.contentClassification`, '');
    this._setContentClassification(flux, pageContentClassification);
    this._setMetaTags();
  }

  componentDidMount() {
    const { configPath } = this.props;
    const { flux, config } = this.context;

    const pageBrowserTitle = _.get(config, `${configPath}.browserTitle`, '');
    const pageType = _.get(config, `${configPath}.pageType`, '');
    const pageCustomScript = _.get(config, `${configPath}.customScript`);

    flux.getActions('Helmet').updateRouteTitle(pageBrowserTitle);

    this._setPageType(flux, pageType);

    if (pageCustomScript && window) {
      const $ = window['$'];

      /**
      ** Because the script tags aren't working when using dangerouslySetInnerHTML.
      *  So for shortest way, we use jquery to append the specific scripts to Page

      ** Using replace function to restore <//script> back to </script>
      */
      $('.Page').append(unescapeScript(pageCustomScript));
    }


    // TODO: Deprecate this code block when CSS position: sticky is supported all browsers
    if (window && this._isStickyHeader() && this._hasHeader()) {
      if (!isPositionStickySupported()) {
        const header = document.getElementById('header');
        const page = document.getElementById('page');

        const onScrollForStickyHeader = () => {
          const pageYOffset = window.pageYOffset;
          const paddingTop = header.getBoundingClientRect().top + header.offsetHeight - pageYOffset;

          if (getComputedStyle(header).position !== 'fixed') {
            header.style.position = 'fixed';
            if (paddingTop > 0) {
              page.style.paddingTop = paddingTop + 'px';
            }
          }
        };

        window.addEventListener('scroll', onScrollForStickyHeader);
        window.addEventListener('resize', onScrollForStickyHeader);

        this._stickyHeaderOnScrollListener = onScrollForStickyHeader;
      }
    }
  }

  componentWillUnmount() {
    const { flux } = this.context;
    flux.getActions('Helmet').update({ title: '' });

    this._setContentClassification(flux, '');
    this._setPageType(flux, '');

    // TODO: Deprecate this code block when CSS position: sticky is supported all browsers
    if (this.stickyHeaderOnScrollListener) {
      window.removeEventListener('scroll', this._stickyHeaderOnScrollListener);
      window.removeEventListener('resize', this._stickyHeaderOnScrollListener);
    }
  }

  _setContentClassification(flux, contentClassification) {
    flux.getActions('ContentClassification').updateRouteContentClassification(contentClassification);
  }

  _setMetaTags() {
    const { configPath } = this.props;    
    const {flux, config} = this.context;
    const metaTags = _.get(config, `${configPath}.meta`, {});
    
    if (!_.isEmpty(metaTags)) {
      const {
        title,
        description,
        keywords,
        robots
      } = metaTags;

      const metas = buildMetaTags({title, description, keywords, robots});
      flux.getActions('Helmet').updateMetas({ metas });
    }
  }

  _setPageType(flux, pageType) {
    flux.getActions('Advertisement').updateRouteTargetingKeyValue('wnpt', pageType);
  }

  // TODO: Remove once the weather page no longer has to be in iframe.
  _isInIframe() {
    let ret;

    try {
      ret = window.self !== window.top;
    } catch (e) {
      ret = true;
    }

    return ret;
  }

  _setGlobalTrackingVar(affiliateName, affiliateId) {
    const wngPageInfo = (typeof window !== 'undefined' && window["wng_pageInfo"]) || {};

    wngPageInfo.affiliateName = affiliateName;
    wngPageInfo.affiliateId = affiliateId;

    //Set the new wng_pageInfo variable for use by external parties
    return wngPageInfo;
  }

  _removeGlobalTrackingVar() {
    const wngPageInfo = (window && window["wng_pageInfo"]) || {};

    delete wngPageInfo.affiliateName;
    delete wngPageInfo.affiliateId;

    return wngPageInfo;
  }

  _isStickyHeader() {
    const { configPath } = this.props;
    const { config } = this.context;

    const { appOptions: { headerSticky: appHeaderSticky } = {} } = config;
    const pageHeaderSticky = _.get(config, `${configPath}.headerSticky`);

    return pageHeaderSticky === undefined ? appHeaderSticky : pageHeaderSticky;
  }

  _hasHeader() {
    const { configPath } = this.props;
    const { config } = this.context;
    const { header } = _.get(config, configPath, {});

    return !(_.isArray(header) && header.length === 0);
  }

  render() {
    const { params, configPath, body } = this.props;
    const {
      config: {
        affiliate: {
          name: affiliateName = ''
        } = {}
      } = {},
      config
    } = this.context;

    const enableStickyHeader = this._isStickyHeader() && this._hasHeader();

    const { appOptions: { gutterSpacing: appGutterSpacing } = {} } = config;
    const pageGutterSpacing = _.get(config, `${configPath}.gutterSpacing`);
    const gutterSpacing = pageGutterSpacing || appGutterSpacing;

    const pageCustomStyle = _.get(config, `${configPath}.customStyle`);

    let { header, footer } = _.get(config, configPath, {});
    let headerConfigPath = 'header';
    let footerConfigPath = 'footer';

    if (header) {
      headerConfigPath = `${configPath}.${headerConfigPath}`;
    } else {
      header = _.get(config, headerConfigPath, []);
    }

    // WEB-4964: decode header menu
    header = decodeTextOfMenuItem(header, '[0].cols[0].components');

    if (footer) {
      footerConfigPath = `${configPath}.${footerConfigPath}`;
    } else {
      footer = _.get(config, footerConfigPath, []);
    }

    // WEB-4964: decode footer menu
    footer = decodeTextOfMenuItem(footer, '[0].cols[0].components');

    const children = [];

    if (_.isArray(header)) {
      const headerClassnames = classNames(
        'PageHeader',
        {
          'PageHeader--sticky': enableStickyHeader
        }
      );

      children.push(
        <ConfigGrid key={children.length} fullwidth configPath={headerConfigPath} id="header" className={headerClassnames} params={params} />
      );
    } else if (_.isObject(header)) {
      children.push(
        <Header {...header} />
      );
    }

    if (body) {
      children.push(body);
    } else {
      children.push(
        <ConfigGrid key={children.length} configPath={`${configPath}.body`} gutterSpacing={gutterSpacing} className="PageBody" params={params} />
      );
    }

    if (_.isArray(footer)) {
      children.push(
        <ConfigGrid key={children.length} fullwidth configPath={footerConfigPath} className="PageFooter" params={params} />
      );
    } else if (_.isObject(footer)) {
      children.push(
        <Footer {...footer} />
      );
    }

    const className = classNames(
      'Page',
      affiliateName ? 'affiliate--' + affiliateName.toLowerCase() : null,
      {
        'is-inIframe': this._isInIframe()
      }
    );

    return (
      <div id="page" className={className}>
        { pageCustomStyle && <div dangerouslySetInnerHTML={ {__html: pageCustomStyle} }></div> }
        { children }
      </div>
    );
  }
}

export default Page;



// WEBPACK FOOTER //
// ./app/components/core/Page.js